import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useListTeamHomeVenues } from "../api/TeamVenueApiProvider";
import { useListPitches } from "../api/VenueApiProvider";
import { FormActions } from "../utils/FormActions";
import { FixtureRowFields } from "./FixtureRowFields";
export const LeagueMatchForm = ({ divisions, leagueVenues, onSubmit, leagueMatch, isSubmitting = false, }) => {
    const methods = useForm({
        defaultValues: {
            division: leagueMatch?.division ?? divisions[0]?.id,
            homeTeam: leagueMatch?.homeTeam?.id,
            awayTeam: leagueMatch?.awayTeam?.id,
            kickOff: leagueMatch?.kickOff,
            venue: leagueMatch?.venue?.id ?? null,
            pitch: leagueMatch?.pitchName ? undefined : null,
            round: leagueMatch?.round ?? 1,
            matchType: leagueMatch?.matchType ?? "regular",
            playoffRound: leagueMatch?.playoffRound ?? null,
        },
    });
    const { control, handleSubmit, watch, setValue, formState: { errors }, } = methods;
    const selectedVenueId = watch("venue");
    const homeTeamId = watch("homeTeam");
    const awayTeamId = watch("awayTeam");
    const { data: homeTeamVenues = [], isLoading: homeVenuesLoading } = useListTeamHomeVenues({
        teamId: homeTeamId,
        enabled: !!homeTeamId,
    });
    const { data: awayTeamVenues = [], isLoading: awayVenuesLoading } = useListTeamHomeVenues({
        teamId: awayTeamId,
        enabled: !!awayTeamId,
    });
    const venuesLoading = homeVenuesLoading || awayVenuesLoading;
    const activeHomeTeamVenues = homeTeamVenues.filter((tv) => tv.isActive);
    const activeAwayTeamVenues = awayTeamVenues.filter((tv) => tv.isActive);
    const venues = Array.from(new Map([
        ...leagueVenues.map((lv) => lv.venue),
        ...activeHomeTeamVenues.map((tv) => tv.venue),
        ...activeAwayTeamVenues.map((tv) => tv.venue),
    ].map((v) => [v.id, v])).values());
    // Set venue default from team venues once they load (for editing)
    const hasVenueBeenSet = useRef(!!leagueVenues.find((lv) => lv.venue.id === leagueMatch?.venue?.id));
    useEffect(() => {
        if (leagueMatch?.venue && !hasVenueBeenSet.current) {
            const teamVenue = activeHomeTeamVenues.find((tv) => tv.venue.id === leagueMatch.venue.id) ??
                activeAwayTeamVenues.find((tv) => tv.venue.id === leagueMatch.venue.id);
            if (teamVenue) {
                setValue("venue", teamVenue.venue.id);
                hasVenueBeenSet.current = true;
            }
        }
    }, [homeTeamVenues, awayTeamVenues, leagueMatch, setValue]);
    const { data: pitches = [] } = useListPitches({
        venueId: selectedVenueId ?? undefined,
        enabled: !!selectedVenueId,
    });
    const filteredPitches = selectedVenueId
        ? pitches.filter((p) => p.venue === selectedVenueId)
        : [];
    // Set pitch default value when pitches load for editing
    const hasPitchBeenSet = useRef(false);
    useEffect(() => {
        if (leagueMatch?.pitchName &&
            pitches.length > 0 &&
            !hasPitchBeenSet.current) {
            const matchPitch = pitches.find((p) => p.name === leagueMatch.pitchName);
            if (matchPitch) {
                setValue("pitch", matchPitch.id);
                hasPitchBeenSet.current = true;
            }
        }
    }, [pitches, leagueMatch, setValue]);
    return (_jsx(FormProvider, { ...methods, children: _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, sx: { mt: 1 }, children: [_jsx(FixtureRowFields, { errors: errors, divisions: divisions }), _jsx(Typography, { variant: "subtitle2", sx: { mt: 2, mb: 1 }, color: "text.secondary", children: "Venue & Pitch (optional)" }), _jsx(Controller, { name: "venue", control: control, render: ({ field }) => (_jsx(Autocomplete, { options: venues, getOptionLabel: (option) => option.name, value: venues.find((v) => v.id === field.value) ?? null, onChange: (_, newValue) => field.onChange(newValue?.id ?? null), loading: venuesLoading, disabled: venuesLoading, renderInput: (params) => (_jsx(TextField, { ...params, margin: "normal", fullWidth: true, label: "Venue" })) })) }), selectedVenueId && filteredPitches.length > 0 && (_jsx(Controller, { name: "pitch", control: control, render: ({ field }) => (_jsx(Autocomplete, { options: filteredPitches, getOptionLabel: (option) => option.name, value: filteredPitches.find((p) => p.id === field.value) ?? null, onChange: (_, newValue) => field.onChange(newValue?.id ?? null), renderInput: (params) => (_jsx(TextField, { ...params, margin: "normal", fullWidth: true, label: "Pitch" })) })) })), _jsx(FormActions, { submitLabel: leagueMatch === undefined ? "Create Match" : "Update Match", isSubmitting: isSubmitting, submitDisabled: venuesLoading })] }) }));
};
